import React from 'react';
import { Form } from 'antd';
import dayjs from 'dayjs';

import { clearFieldErrors, handleFormErrors } from '../../common/errorHelpers';
import { ReminderTypeEnum } from '../../common/types';
import { useAddReminder } from '../../common/useAddReminder';
import { useGetAllUsers } from '../../common/useGetAllUsers';
import { FormDatePicker } from '../../components/forms/FormDatePicker';
import { FormInput } from '../../components/forms/FormInput';
import { FormSelect } from '../../components/forms/FormSelect';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { SaveButtonIcon } from '../dictionaries/DictionaryItemsList';
import { RecruitmentHistoryType } from '../recruitments/Recruitments.types';

import { ClearButton, SubmitButton } from './RecruitmentDataForm';

type ReminderFormProps = {
  recruitment: RecruitmentHistoryType;
};

export const ReminderForm: React.FC<ReminderFormProps> = ({ recruitment }) => {
  const [form] = Form.useForm();
  const addReminder = useAddReminder();
  const { data: usersData, isLoading: usersLoading } = useGetAllUsers();

  if (!usersData) return <></>;

  const usersOptions =
    !usersLoading && usersData
      ? usersData.results.map((user) => ({
          value: user.id,
          content: `${user.firstName} ${user.lastName}`,
        }))
      : [];

  const reminderTypeOptions = Object.values(ReminderTypeEnum).map((reminderType) => ({
    value: reminderType,
    content: reminderType,
  }));

  const handleSubmit = () => {
    const { date, text, title, reminderType, user } = form.getFieldsValue();
    const payload = {
      data: {
        active: true,
        date: dayjs(date).unix(),
        objectClass: 'RECRUITMENT',
        objectUuid: recruitment.uuid,
        text: text,
        title: title,
        type: reminderType.value.toUpperCase(),
        user: user,
      },
    };
    addReminder.mutate(payload, {
      onSuccess: () => clearFieldErrors(form),
      onError: (error) => handleFormErrors({ error, form }),
    });
  };

  const intialValues = {
    user: usersOptions[0],
    reminderType: reminderTypeOptions[0],
    date: dayjs(),
  };

  return (
    <Form form={form} initialValues={intialValues} layout="vertical" colon={false}>
      <StyledColumn gap="0.3rem">
        <FormInput name="title" label="Title" />

        <FormTextArea name="text" label="Note" rows={10} />

        <FormSelect name="user" label="User" allowClear={false} options={usersOptions} />

        <FormDatePicker name="date" label="Remind at" />

        <FormSelect name="reminderType" label="Reminder type" allowClear={false} options={reminderTypeOptions} />
      </StyledColumn>

      <StyledRow justifyContent="flex-end" marginTop="14px">
        <ClearButton onClick={() => form.resetFields()}>Clear</ClearButton>
        <SubmitButton onClick={handleSubmit} icon={<SaveButtonIcon />}>
          Save
        </SubmitButton>
      </StyledRow>
    </Form>
  );
};
